import { CSSProperties, ReactNode, useMemo, useState } from 'react';
import { ColDef, GridApi } from 'ag-grid-community';
import { AdminHeaderView } from '../layout/header/admin-header.view';
import { AdminHeaderTitleView } from '../layout/header/admin-header-title.view';
import { ExportCsvButtonView } from '../buttons/export-csv-button.view';
import { GridView } from './grid.view';
import { ButtonView } from '../buttons/button.view';
import { background } from 'storybook/theming';
import { types } from 'sass';
import Color = types.Color;
import { Size } from '@vivli/shared/theme';

const containerStyle: CSSProperties = {
    padding: '0px 40px 20px 40px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative'
};

const titleContainerStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr auto auto',
    columnGap: '20px',
    marginBottom: '0'
};

const headerBtnsStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    gap: '10px'
};

const subHeaderStyle: CSSProperties = {
    width: '100%',
    height: '100',
    marginTop: '-20px',
    paddingBottom: Size.PADDING * 1.3,
    color: 'rgb(96, 104, 111)'
};

interface GridPageComponentProps {
    title: string;
    exportTitle: string;
    subHeader?: string;
    columnDefs: ColDef[];
    rowData: any[];
    onRowClick?: (data: any) => void;
    fullWidthColumns?: boolean;
    asyncUpdateKey?: any;
    dataRefreshing?: boolean;
    onGridReady?: (gridApi: GridApi) => void;
    customHeaderButtons?: ReactNode;
    customHeader?: ReactNode;
    customComponent?: ReactNode;
    customHeaderHideShowButton?: string;
    flushPrevious?: boolean;
    exportColumnKeys?: string[];
    ignoreClickForFields?: string[];
    exportToCsvCustom?: () => void;
    isExportingCustom?: boolean;
}

export const GridPageView = ({
    columnDefs,
    rowData,
    onRowClick,
    title,
    exportTitle,
    subHeader,
    fullWidthColumns,
    dataRefreshing,
    onGridReady,
    customHeader,
    customComponent,
    flushPrevious,
    exportColumnKeys,
    customHeaderHideShowButton,
    customHeaderButtons,
    ignoreClickForFields,
    exportToCsvCustom,
    isExportingCustom
}: GridPageComponentProps) => {
    const [gridApi, setGridApi] = useState<GridApi>(null);
    const [showCustomHeader, setShowCustomHeader] = useState(true);

    const customHeaderContainerStyle = useMemo<CSSProperties>(() => {
        return {
            boxShadow: 'none',
            height: showCustomHeader ? '100%' : 0,
            visibility: showCustomHeader ? 'visible' : 'hidden',
            marginBottom: showCustomHeader ? '20px' : '0px'
        };
    }, [showCustomHeader]);

    const exportToCsv = () => {
        const dateStr = new Date().toLocaleDateString().replace('/', '_');
        const fileName = `${exportTitle}_${dateStr}`;
        if (exportToCsvCustom) {
            exportToCsvCustom();
        } else {
            gridApi.exportDataAsCsv({ fileName, columnKeys: exportColumnKeys });
        }
    };
    const handleOnGridReady = (gridApi: GridApi) => {
        setGridApi(gridApi);
        onGridReady && onGridReady(gridApi);
    };

    return (
        <div style={containerStyle}>
            <AdminHeaderView style={{ margin: '20px 0 0 0', gap: '0' }} isCustomHeaderVisible={showCustomHeader}>
                <div style={titleContainerStyle}>
                    <AdminHeaderTitleView title={title} />
                    <div style={headerBtnsStyle}>
                        {customHeaderButtons}
                        <ExportCsvButtonView onClick={exportToCsv} isLoading={isExportingCustom ?? false} />
                    </div>
                    {customHeader && customHeaderHideShowButton && (
                        <ButtonView onClick={() => setShowCustomHeader(!showCustomHeader)}>
                            {showCustomHeader ? 'Hide' : 'Show'} {customHeaderHideShowButton}
                        </ButtonView>
                    )}
                </div>
                {subHeader && <div style={subHeaderStyle}>{subHeader}</div>}
                {customHeader && <div style={customHeaderContainerStyle}>{customHeader}</div>}
            </AdminHeaderView>

            {customComponent && <div>{customComponent}</div>}
            <GridView
                rowData={rowData}
                columns={columnDefs}
                onRowClick={onRowClick}
                onGridReady={handleOnGridReady}
                fullWidthColumns={fullWidthColumns}
                style={{ height: '90%' }}
                flushPrevious={flushPrevious}
                ignoreClickForFields={ignoreClickForFields}
            />
        </div>
    );
};
